// Anatomical landmark resolver — turns a diagnosis description into a PRECISE
// avatar position (figure space) + the system (layer) to reveal + the coarse region
// it belongs to + a short sub-location label. This is the SINGLE SOURCE OF TRUTH the
// avatar (red/green markers) AND the Wellness "Conditions tracked" list both read,
// so a condition's label always matches where its marker sits on the body.
//
// WHERE PARTS ARE comes from anatomyIndex.ts, GENERATED by scripts/index-anatomy.mjs
// from the actual models in app/public/anatomy: every part names the layer that
// contains it and a figure-space position that was either MEASURED from that
// layer's geometry (eyes, brain) or carried over from the original landmark table
// and then VERIFIED against the layer's meshes. A disease rule below can only map
// to a part key — it cannot invent a position or a layer — so the avatar never
// reveals a system that does not show the diseased structure. anatomyIndex.test.ts
// enforces this for every rule.
//
// Figure space: the avatar fits the body to 1.6 units (head ≈ +0.85, feet ≈ -0.75);
// part positions use patient-RIGHT = +x, flipped to the renderer's frame on output.
import type { AnatomyLayer, BodyRegion } from './types';
import ANATOMY_INDEX from './anatomyIndex.ts';

export type Landmark = { pos: [number, number, number]; system: AnatomyLayer };
// Richer placement: everything the UI needs to co-locate a condition with its
// marker — the coarse region (for grouping), the system to reveal, and a short
// human sub-location (e.g. "T7–T12", "psoas", "septal defect").
export type Placement = { pos: [number, number, number]; system: AnatomyLayer; region: BodyRegion; detail: string; part: PartKey };

export type PartKey = keyof typeof ANATOMY_INDEX.parts;
type Part = { layer: string; pos: readonly number[]; sideOffsetX?: number; verified: boolean; source: string };
export const PARTS = ANATOMY_INDEX.parts as Record<PartKey, Part>;

// Coarse region each part belongs to (for the Wellness grouping / avatar hotspots).
export const PART_REGION: Record<PartKey, BodyRegion> = {
  eyes: 'head', brain: 'head', heart: 'heart', septum: 'heart', arteries: 'heart', lungs: 'chest', thyroid: 'thyroid',
  liver: 'abdomen', spleen: 'abdomen', pancreas: 'abdomen', kidney: 'abdomen', bladder: 'pelvis', spine: 'spine',
  psoas: 'abdomen', paraspinal: 'spine', trunk: 'abdomen',
};

/** Place a condition on an indexed part: its layer, its position (mirrored to the
 *  named side for paired organs), the region and a label. */
function at(part: PartKey, sx: number, detail: string, region: BodyRegion = PART_REGION[part]): Placement {
  const p = PARTS[part];
  const x = sx && p.sideOffsetX ? p.sideOffsetX * sx : p.pos[0];
  return { pos: [x, p.pos[1], p.pos[2]], system: p.layer as AnatomyLayer, region, detail, part };
}

// Figure-space Y per vertebral level (interpolated from the indexed spine column).
const SPINE_Y: Record<string, number> = (() => {
  const m: Record<string, number> = {};
  const lerp = (a: number, b: number, n: number, i: number) => a + ((b - a) * i) / (n - 1);
  for (let i = 0; i < 7; i++) m['C' + (i + 1)] = lerp(0.63, 0.51, 7, i);   // cervical
  for (let i = 0; i < 12; i++) m['T' + (i + 1)] = lerp(0.50, 0.315, 12, i); // thoracic
  for (let i = 0; i < 5; i++) m['L' + (i + 1)] = lerp(0.30, 0.20, 5, i);    // lumbar
  m['S1'] = 0.16;
  return m;
})();

// Patient-right is +x (the heart, on the patient's left, indexes at -x).
function side(desc: string): number {
  if (/\bright\b/i.test(desc)) return 1;
  if (/\bleft\b/i.test(desc)) return -1;
  return 0;
}
function sideWord(sx: number): string { return sx > 0 ? 'right ' : sx < 0 ? 'left ' : ''; }
function levels(desc: string): string[] {
  return (desc.toUpperCase().match(/\b([CTL])(1[0-2]|[1-9])\b/g) || []).map((s) => s.replace(/\s+/g, ''));
}
function spineY(desc: string): number | null {
  const lv = levels(desc).map((l) => SPINE_Y[l]).filter((y) => y != null);
  return lv.length ? lv.reduce((a, b) => a + b, 0) / lv.length : null;
}
// Cervical/thoracic map to the "spine" region box; lumbar/sacral to "lumbar".
function spineRegion(lv: string[]): BodyRegion {
  return lv.some((l) => l[0] === 'L' || l === 'S1') ? 'lumbar' : 'spine';
}
// Short level label: single level, or first–last range (e.g. T7–T12).
function levelLabel(lv: string[]): string {
  if (!lv.length) return '';
  return lv.length === 1 ? lv[0] : `${lv[0]}–${lv[lv.length - 1]}`;
}

// The base model renders with +x = patient-LEFT (it faces the camera, so a
// structure on the patient's left — heart/ASD — appears on the viewer's RIGHT;
// this matches the avatar's own REGION table, e.g. left arm at +x). The index and
// the rules below use the OPPOSITE convention, so resolveLandmark() flips x.
export function resolveLandmark(desc: string): Landmark | null {
  const p = resolveRaw(desc);
  return p ? { pos: [0 - p.pos[0], p.pos[1], p.pos[2]], system: p.system } : null;
}
// Full placement (region + system + sub-location + figure-space position), used by
// the Wellness conditions list so grouping/labels stay in lock-step with the avatar.
export function describeAnatomy(desc: string): Placement | null {
  const p = resolveRaw(desc);
  return p ? { ...p, pos: [0 - p.pos[0], p.pos[1], p.pos[2]] } : null;
}
/** The indexed part a description resolves to (for tests / audits). */
export function partFor(desc: string): PartKey | null { return resolveRaw(desc)?.part ?? null; }

function resolveRaw(desc: string): Placement | null {
  const t = (desc || '').toLowerCase();
  const sx = side(desc);
  const sw = sideWord(sx);

  // Psoas (a paravertebral muscle) — lower lumbar, lateral, slightly posterior.
  if (/psoas/.test(t)) return at('psoas', sx || 1, `${sw}psoas`.trim());

  // Spine levels named (T7-T12, T7-L2, L4-L5, …): the vertebral column part, with
  // y interpolated to the level and soft tissue hugging it in the muscle layer.
  const lv = levels(desc);
  const y = spineY(desc);
  if (y != null) {
    const label = levelLabel(lv);
    if (/paraspinal|phlegmon|abscess|myositis|epidural|soft tissue/.test(t)) { const p = at('paraspinal', sx || 1, label ? `paraspinal ${label}` : 'paraspinal', spineRegion(lv)); p.pos[1] = y; return p; }
    const p = at('spine', 0, label || 'vertebral', spineRegion(lv)); p.pos[1] = y; return p;
  }
  if (/osteomyel|vertebr|spondyl|\bdisc\b|stenosis|scoliosis|kyphosis|fracture/.test(t)) return at('spine', 0, 'vertebral');
  // Spine soft-tissue with NO named level stays ON the spine at mid-thoracic rather
  // than falling back to a coarse (often mislabeled) region box.
  if (/paraspinal|paravertebral|perispinal|epidural|phlegmon|myositis/.test(t)) return at('paraspinal', sx || 1, 'paraspinal');

  // Eyes live in the NERVES layer (the only model with head structures besides skin).
  if (/retin|macul|\beyes?\b|ophthalm|optic|fundus|glaucoma|cataract/.test(t)) return at('eyes', sx, sx ? `${sw}eye`.trim() : 'eyes');

  // Heart & great vessels live in the circulatory (vessels) model, not the organs model.
  if (/\basd\b|atrial septal|septal defect|congenital heart|vsd\b/.test(t)) return at('septum', 0, 'septal defect');
  if (/heart|cardi|fibrillation|arrhythm|angina|myocard/.test(t)) return at('heart', 0, 'heart');

  // Organs
  if (/lung|pleur|pneumon|asthma|copd|bronch/.test(t)) return at('lungs', sx || 1, `${sw}lung`.trim());
  if (/kidney|renal|nephro|albuminuria/.test(t)) return at('kidney', sx || 1, `${sw}kidney`.trim());
  if (/liver|hepat|steatosis|fatty liver|nafld|masld/.test(t)) return at('liver', 0, 'liver');
  if (/spleen|splen/.test(t)) return at('spleen', 0, 'spleen');
  if (/bladder/.test(t)) return at('bladder', 0, 'bladder');
  if (/thyroid/.test(t)) return at('thyroid', 0, 'thyroid');
  if (/brain|cerebr|crani|intracran|stroke|migraine|epilep/.test(t)) return at('brain', 0, 'brain');

  // Metabolic / cardiovascular conditions that have an organ but no lesion: place
  // the marker on the organ that owns the disease and reveal the layer that shows it.
  // (Ordered AFTER kidney/liver/eye so "diabetic kidney disease" stays on the kidney.)
  if (/pancrea|diabet|insulin|glyc(a|ae)?mi/.test(t)) return at('pancreas', 0, 'pancreas');
  if (/hypertens|blood pressure|\bhtn\b/.test(t)) return at('heart', 0, 'heart & arteries');
  if (/lipid|cholesterol|triglycer|atheroscl|\bldl\b/.test(t)) return at('arteries', 0, 'arteries');
  if (/obes|overweight|\bbmi\b|adipos/.test(t)) return at('trunk', 0, 'body composition');
  return null;
}
